
import { forwardRef, useRef, useState, useImperativeHandle, useEffect} from "react";
import { AMModal, dBConn, allowedFileFormats} from '../../../ApiCenter/API_GET';
import { set } from "lodash";

const buttonWidth = 90;

export const FileManagerModals = forwardRef((props, ref) => {

    const defaultSettings = {
        showModal: false,
        opacity: 1,
        executeOp: false
    }

    const currentData = useRef(null)
    const gridDataRef = useRef(null) // Struttura gerarchica già creata
    const originalName = useRef(null) // Save the original name before the modification
    const renameInputRef = useRef(null) // Ref per il campo di input del rename

    const [modalSettings, setModalSettings] = useState(defaultSettings)
    
    // focus on field
    useEffect(() => {
        if (modalSettings.showModal && modalSettings.title === 'Rename' && renameInputRef.current) {
            setTimeout(() => {
                const input = renameInputRef.current;
                input?.focus();
                // Posiziona il cursore alla fine del testo
                const length = input?.value?.length || 0;
                input?.setSelectionRange(length, length);
            }, 100);
        }
    }, [modalSettings.showModal, modalSettings.title])

    useImperativeHandle(ref, () => ({
        openModals: (props) => {

            currentData.current = props.dataInTable
            gridDataRef.current = props.gridData  // Save the past hierarchical structure
            // Save the original name before the user changes it
            originalName.current = props.data[0]?.name

            setModalSettings(prev => ({
                ...prev,
                showModal: true,
                title: props.operation,
                data: props.data
            }))
        }
    }));

    // Recursive function to find all the files in a folder
    const findAllFilesInFolder = (folderId, gridData) => {
        const files = [];
        const foldersToDelete = [];
        
        const findRecursive = (parentId) => {
            gridData.forEach(item => {
                if (item.parentId === parentId) {
                    if (item.isFile) {
                        files.push(item);
                    } else {
                        // It's a folder, add to the list and look for recursively
                        foldersToDelete.push(item);
                        findRecursive(item.id);
                    }
                }
            });
        };
        
        findRecursive(folderId);
        return { files, foldersToDelete };
    };

    const confirmOperation = () => {

        if(modalSettings.title === "Rename"){

            const element = modalSettings.data[0];
            const oldName = originalName.current; // Original saved name
            const newName = element.name; // Name modified by the user
       
            // If the name has not changed, do not do anything
            if(oldName === newName){
                setModalSettings(prev => defaultSettings);
                return;
            }
            const objectToDB = {
                operation: 'rename',
                items: []
            };

            setModalSettings(prev => ({...prev, opacity: 0.5}));

            // Usa la struttura gerarchica già creata invece di rigenerarla
            const gridData = gridDataRef.current;

            if(element.isFile === true){
                // RENAME FILE
                const sourceTarget = element.filterPath.startsWith('Source') ? 'source' : 'target';
                const fileInData = currentData.current[`${sourceTarget}Files`].find(f => f.id === element.customData.id);
                
                if(fileInData){
                    // Complete original route from the DB (it could have the initial slash removed from sanitizedsourcefespath)
                    const oldFullPath = sourceTarget === 'source' 
                        ? fileInData.original_fullname 
                        : `${fileInData.link_string}${fileInData.file_extension}`;

                    // Extract the directory and the file name
                    const lastSlashIndex = oldFullPath.lastIndexOf('/');
                    const oldFileNameOnly = lastSlashIndex !== -1 ? oldFullPath.substring(lastSlashIndex + 1) : oldFullPath;
                    const directory = lastSlashIndex !== -1 ? oldFullPath.substring(0, lastSlashIndex + 1) : '';
                    
                    // if we are in Flat Mode (Newname already contains the path) or Structured Mode
                    const isFlat = newName.includes('/');
                    
                    // Builds the new complete path
                    let newFullPath = isFlat ? newName : (directory + newName);
                    
                    // IMPORTANTE: Il DB deve mantenere lo slash iniziale, quindi lo aggiungiamo se non c'è
                    // (sanitizedSourceFilesPath lo rimuove per il frontend, ma il DB lo richiede)
                    const needsLeadingSlash = !newFullPath.startsWith('/');
                    const newFullPathForDB = needsLeadingSlash ? '/' + newFullPath : newFullPath;

                    // Update local data (senza slash per il frontend)
                    if(sourceTarget === 'source'){
                        fileInData.original_fullname = newFullPath;
                    } else {
                        // Per target, rimuovi l'estensione se presente nel nuovo nome
                        const newNameWithoutExt = newName.replace(fileInData.file_extension, '');
                        const newTargetPath = isFlat ? newNameWithoutExt : (directory + newNameWithoutExt);
                        fileInData.link_string = newTargetPath;
                    }

                    objectToDB.items.push({
                        type: 'file',
                        sourceTarget: sourceTarget,
                        id: fileInData.id,
                        oldName: oldFileNameOnly,  // Just the file name
                        newName: newName,
                        oldFullPath: oldFullPath,  // Original full path (without leading slash)
                        newFullPath: newFullPathForDB   // New full path to the DB (WITH leading slash)
                    });
                } else {
                    //console.log("File not found in data!");
                }
            } else if(element.isFile === false) {
                // RENAME FOLDER
                const sourceTarget = element.filterPath.startsWith('Source') ? 'source' : 'target';
                
                // Check if it's a Package
                if(element.im === 'Package'){
                    const packageId = element.customData?.package_id || element.customData?.id;
                    
                    // Update local data - packages are nested in project.batches[].packages[]
                    let packageInData = null;
                    currentData.current.sourcePackages.forEach(project => {
                        project.batches?.forEach(batch => {
                            const pkg = batch.packages?.find(p => p.package_id === packageId);
                            if(pkg){
                                packageInData = pkg;
                            }
                        });
                    });
                    
                    if(packageInData){
                        packageInData.upload_description = newName;
                    }
                    
                    objectToDB.items.push({
                        type: 'package',
                        sourceTarget: sourceTarget,
                        im: element.im,
                        customData: {
                            ...element.customData,
                            id: packageId
                        },
                        oldName: oldName,
                        newName: newName
                    });
                }
                // Check if it's a Tag folder
                else if(element.customData?.tag_id !== undefined){
                    // RENAME TAG FOLDER
                
                    const tagId = element.customData.tag_id;
                    // Update the tag_name of all files that have this tag_id
                    const filesWithTag = currentData.current[`${sourceTarget}Files`].filter(f => f.tag_id === tagId);
                 
                    filesWithTag.forEach(file => {
                        file.tag_name = newName;
                    });
                    
                    objectToDB.items.push({
                        type: 'tag',
                        sourceTarget: sourceTarget,
                        tag: {
                            tag_id: tagId,
                            oldName: oldName,
                            newName: newName
                        },
                        filesUpdated: filesWithTag.length
                    });
                }
                // Virtual folder created by the user (checks both uploadedstructure and IM)
                else if(element.uploadedStructure === true || (element.im === null && element.uploadedStructure !== false)){
                    // Find all the files in the recursively folder
                    const { files } = findAllFilesInFolder(element.id, gridData);
                    
                    // Calculate the folder path to be replaced
                    const oldFolderName = oldName; 
                 
                    // Find the relative path by looking at the files in the folder
                    // the files already have the correct Path in Link_string/Original_Fullname
                    let oldPathPrefix = oldFolderName;
                    let newPathPrefix = newName;
                    
                    // If there are files, use their path to understand the structure
                    if(files.length > 0){
                        const firstFile = files[0];
                        const firstFileData = currentData.current[`${sourceTarget}Files`].find(f => f.id === firstFile.customData.id);
                        if(firstFileData){
                            const fieldName = sourceTarget === 'source' ? 'original_fullname' : 'link_string';
                            const filePath = firstFileData[fieldName];
                          
                            // Extract the folder path from the file of the file
                            if(filePath && filePath.includes('/')){
                                const parts = filePath.split('/');
                                // Find where Oldfoldername is located in the path
                                const folderIndex = parts.findIndex(p => p === oldFolderName);
                                if(folderIndex !== -1){
                                    // Reconstruct the path to the folder
                                    oldPathPrefix = parts.slice(0, folderIndex + 1).join('/');
                                    const pathBeforeFolder = parts.slice(0, folderIndex).join('/');
                                    newPathPrefix = pathBeforeFolder ? `${pathBeforeFolder}/${newName}` : newName;
                                }
                            }
                        }
                    }

                    // Update all the files that this Path have
                    const filesToUpdate = [];

                    files.forEach((file, index) => {
                        //console.log(`File ${index}:`, file.name, 'customData.id:', file.customData?.id);
                        const fileData = currentData.current[`${sourceTarget}Files`].find(f => f.id === file.customData.id);
                        if(fileData){
                            const fieldName = sourceTarget === 'source' ? 'original_fullname' : 'link_string';
                            const oldPath = fileData[fieldName];
                          
                            if(oldPath && oldPath.startsWith(oldPathPrefix)){
                                const newPath = oldPath.replace(oldPathPrefix, newPathPrefix);
                                fileData[fieldName] = newPath;
                            
                                filesToUpdate.push({
                                    id: fileData.id,
                                    oldPath: oldPath,
                                    newPath: newPath
                                });
                            } else {
                                //console.log(`  - NOT UPDATED (path doesn't match)`);
                            }
                        } else {
                            //console.log(`  - File not found in ${sourceTarget}Files!`);
                        }
                    });

                    // Add the initial slash for the database (if it is not already there)
                    const oldPathPrefixForDB = oldPathPrefix.startsWith('/') ? oldPathPrefix : '/' + oldPathPrefix;
                    const newPathPrefixForDB = newPathPrefix.startsWith('/') ? newPathPrefix : '/' + newPathPrefix;
                    
                    objectToDB.items.push({
                        type: 'virtualFolder',
                        sourceTarget: sourceTarget,
                        oldFolderName: oldFolderName,
                        newFolderName: newName,
                        oldPathPrefix: oldPathPrefixForDB,
                        newPathPrefix: newPathPrefixForDB,
                        filesUpdated: filesToUpdate
                    });
                } else {
                    // Royal Folder (package, project, etc.) - should not arrive here because of the control
                    /*console.warn('Rename of royal folders (package, project, etc.) blocked by preventive control ');
                    console.warn('element.uploadedStructure:', element.uploadedStructure);
                    console.warn('element.im:', element.im);*/
                    return;
                }
            } else {
                console.error("Element is neither file nor folder!", element);
                return;
            }

            if(objectToDB.items.length === 0){
                return;
            }

            dBConn.post(`filemanager-operation`, objectToDB).then(response => {
                props.fromModals({
                    operation: modalSettings.title,
                    updatedData: currentData.current,
                    operationData: objectToDB
                })
                setModalSettings(prev => defaultSettings)
            });

            return;
        }

        if(modalSettings.title === "Delete"){
          
            const objectToDB = {
                operation: 'Delete',
                type: null, // Will be set to 'file' or 'deleteTargetTag'
                files: {},
                folders: [],
                deletedTags: [] // Reference for deleted tags
            }

            // Use the already created hierarchical structure instead of regenerating it
            const gridData = gridDataRef.current;

            modalSettings.data.forEach(element => {

                // it's a file
                if(element.isFile === true){
                    const sourceTarget = element.filterPath.startsWith('Source') ? 'source' : 'target'
                    if(objectToDB.files[sourceTarget] === undefined){
                        objectToDB.files[sourceTarget] = []
                    }
                    objectToDB.files[sourceTarget].push({
                        id: element.customData.id, 
                        customid: element.id, 
                        name: element.name, 
                        filterPath: element.filterPath
                    })
                    currentData.current[`${sourceTarget}Files`] = currentData.current[`${sourceTarget}Files`].filter(f => f.id !== element.customData.id)
                    
                    // Set type to 'file' if not already set
                    if(!objectToDB.type){
                        objectToDB.type = 'file';
                    }
                }
                // it's a folder
                else {
                    // Check if it's a Tag folder
                    if(element.customData?.tag_id !== undefined){
                        // DELETE TAG - only need file IDs
                        const sourceTarget = element.filterPath.startsWith('Source') ? 'source' : 'target';
                        const tagId = element.customData.tag_id;
                        
                        // Find all files with this tag
                        const filesWithTag = currentData.current[`${sourceTarget}Files`].filter(f => f.tag_id === tagId);
                        
                        // Add only file IDs to deletion
                        if(!objectToDB.files[sourceTarget]){
                            objectToDB.files[sourceTarget] = [];
                        }
                        filesWithTag.forEach(file => {
                            objectToDB.files[sourceTarget].push({
                                id: file.id
                            });
                        });
                        
                        // Add tag reference (for backend info)
                        objectToDB.deletedTags.push({
                            tag_id: tagId,
                            tag_name: element.customData.tag_name || element.name,
                            sourceTarget: sourceTarget,
                            filesCount: filesWithTag.length
                        });
                        
                        // Set type to 'deleteTargetTag'
                        objectToDB.type = 'deleteTargetTag';
                        
                        // Remove files from local data
                        currentData.current[`${sourceTarget}Files`] = currentData.current[`${sourceTarget}Files`].filter(f => f.tag_id !== tagId);
                        
                    } else if(element.im === 'Package'){
                        // DELETE PACKAGE
                        const sourceTarget = element.filterPath.startsWith('Source') ? 'source' : 'target';
                        const packageId = element.customData?.package_id || element.customData?.id;
                        
                        // Initialize packages array if not exists
                        if(!objectToDB.packages){
                            objectToDB.packages = {};
                        }
                        if(!objectToDB.packages[sourceTarget]){
                            objectToDB.packages[sourceTarget] = [];
                        }
                        
                        // Add package to deletion
                        objectToDB.packages[sourceTarget].push({
                            id: packageId,
                            name: element.name
                        });
                        
                        // Set type to 'deleteSourcePackage'
                        objectToDB.type = 'deleteSourcePackage';
                        
                        // Find all files in this package and add to deletion
                        const { files } = findAllFilesInFolder(element.id, gridData);
                        files.forEach(file => {
                            const st = file.filterPath.startsWith('Source') ? 'source' : 'target';
                            if(!objectToDB.files[st]){
                                objectToDB.files[st] = [];
                            }
                            objectToDB.files[st].push({
                                id: file.customData.id,
                                customid: file.id,
                                name: file.name,
                                filterPath: file.filterPath
                            });
                            currentData.current[`${st}Files`] = currentData.current[`${st}Files`].filter(f => f.id !== file.customData.id);
                        });
                        
                    } else {
                        // Normal folder (not a tag or package)
                        // Find the files contained in loop
                        const { files, foldersToDelete } = findAllFilesInFolder(element.id, gridData);
                                
                    // Add all found files to the deletion object
                    files.forEach(file => {
                        const sourceTarget = file.filterPath.startsWith('Source') ? 'source' : 'target';
                        if(objectToDB.files[sourceTarget] === undefined){
                            objectToDB.files[sourceTarget] = [];
                        }
                        objectToDB.files[sourceTarget].push({
                            id: file.customData.id, 
                            customid: file.id, 
                            name: file.name, 
                            filterPath: file.filterPath
                        });
                        // update the initialData
                        currentData.current[`${sourceTarget}Files`] = currentData.current[`${sourceTarget}Files`].filter(f => f.id !== file.customData.id);
                    });
                    
                    // Add the folder and all subfolders to the deletion object
                    objectToDB.folders.push(element.id);
                    foldersToDelete.forEach(folder => {
                        objectToDB.folders.push(folder.id);
                    });
                    }
                }
            });

            setModalSettings(prev => ({...prev, executeOp: true, opacity: 0.9}));


            dBConn.post(`filemanager-operation`, objectToDB).then(response => {
                props.fromModals({
                    operation: modalSettings.title,
                    updatedData: currentData.current,
                    operationData: objectToDB
                })
                setModalSettings(prev => defaultSettings)
            })  
        }
    }

    const onRenameChange = (text) => {
        setModalSettings(prev => ({
            ...prev,
            data: prev.data.map((el, index) => index === 0 ? {...el, name: text} : el)
        }))
    }


    return modalSettings.showModal && <AMModal
        opacity={modalSettings.opacity}
        buttons= {<>
            <button type="button" className="btn btn-secondary btn-sm" title="Cancel" style={{marginRight: '5px', width: buttonWidth}} disabled={modalSettings.opacity !== 1 || modalSettings.executeOp} onClick={() => setModalSettings(prev => defaultSettings)}>Cancel</button>
            {modalSettings.title === "Delete" ? 
                <button type="button" className="btn btn-danger btn-sm" title="Delete" style={{width: buttonWidth}} disabled={modalSettings.opacity !== 1 || modalSettings.executeOp} onClick={confirmOperation}>Delete</button>
                :
                <button type="button" className="btn btn-primary btn-sm" title="Rename" style={{width: buttonWidth}} disabled={modalSettings.opacity !== 1} onClick={confirmOperation}>Rename</button>}
            </>}
        header_title={modalSettings.title}
    >
        {modalSettings.title === "Delete" && (
            modalSettings.data.length === 1 ? 
            
            <div>
                <p>You are deleting {modalSettings.data[0].isFile ? 'the file' : 'the folder'} <b>{modalSettings.data[0].name}</b></p>
                {!modalSettings.data[0].isFile && <p className="text-warning">All files and subfolders inside will be deleted</p>}
                <div style={{marginTop: '5px', marginBottom: '1rem'}}>
                {modalSettings.executeOp === true ? (<>
                    Deletion...
                    <div className="spinner-border text-danger" role="status" style={{width: '20px', height: '20px'}}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                    
                    </>)
                : 'Click Delete to proceed'}
                </div>
            </div> : 
            
            <div>
                <p>You are deleting {modalSettings.data.length} items</p>
                <p className="text-warning">All files and subfolders inside will be deleted</p>
                <div style={{marginTop: '5px', marginBottom: '1rem'}}>
                {modalSettings.executeOp === true ? (<>
                    Deletion...
                    <div className="spinner-border text-danger" role="status" style={{width: '20px', height: '20px'}}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                    
                    </>)
                : 'Click Delete to proceed'}
                </div>
            </div>
        )}

        {modalSettings.title === "Rename" && (() => {
            const element = modalSettings.data[0];
            const isSourceFile = element.isFile && element.filterPath?.startsWith('Source');
            
            // For source files, separate name and extension using Allawedfileformats
            let nameWithoutExt = element.name;
            let extension = '';
            
            if (isSourceFile && element.name) {
                const formats = allowedFileFormats();
                // Look for which extension is present in the file name
                const foundFormat = formats.find(format => {
                    const ext = format.startsWith('.') ? format : '.' + format;
                    return element.name.toLowerCase().endsWith(ext.toLowerCase());
                });
                
                if (foundFormat) {
                    const ext = foundFormat.startsWith('.') ? foundFormat : '.' + foundFormat;
                    nameWithoutExt = element.name.substring(0, element.name.length - ext.length);
                    extension = ext;
                }
            }
            
            return (
                <div>
                    {isSourceFile && extension ? (
                        <div className="input-group">
                            <input 
                                ref={renameInputRef}
                                className="form-control" 
                                type="text" 
                                value={nameWithoutExt} 
                                onChange={(e) => onRenameChange(e.target.value + extension)} 
                                spellCheck={false} 
                                disabled={modalSettings.opacity !== 1}
                            />
                            <span className="input-group-text">{extension}</span>
                        </div>
                    ) : (
                        <input 
                            ref={renameInputRef}
                            className="form-control" 
                            type="text" 
                            value={element.name} 
                            onChange={(e) => onRenameChange(e.target.value)} 
                            spellCheck={false}
                            disabled={modalSettings.opacity !== 1}
                        />
                    )}
                </div>
            );
        })()}
    </AMModal>

})